/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ViewModel } from "../../viewmodel/viewmodel"
import type { SelectedOrgId } from "../organization/organization-types"

import type { UserListService } from "./user-list-service"
import type { UserListStore } from "./user-list-store"
import type { InviteStatus, UserList, UserListFilter } from "./user-types"
import { UserRole } from "./user-types"

export class UserListViewModel implements ViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly listStore: UserListStore,
        private readonly userListService: UserListService
    ) {}

    isValidUserRole = (value: string | null): value is UserRole => {
        return Object.values(UserRole).includes(value as UserRole)
    }

    // Getters
    get hasUsers(): boolean {
        return this.listStore.users.length > 0
    }

    get isLoadingUsers(): boolean {
        return this.listStore.isLoadingUsers
    }

    get currentPage(): number {
        return this.listStore.currentPage
    }

    get pagedUsers(): UserList[] {
        return this.listStore.pagedUsers
    }

    get filter(): UserListFilter {
        return this.listStore.allUsersFilter
    }

    get totalPages(): number {
        return this.listStore.totalPages
    }

    // Setters
    setPage(page: number): void {
        this.listStore.setPage(page)
    }

    setSearchFilter(value: string): void {
        this.listStore.setFilter("search", value)
    }

    setStatusFilter(filter: boolean | null): void {
        this.listStore.setFilter("status", filter)
    }

    setRolesFilter(roles: UserRole[]): void {
        this.listStore.setFilter("roles", roles)
    }

    setExternalFilter(filter: boolean | null): void {
        this.listStore.setFilter("external", filter)
    }

    resetFilters(): void {
        this.listStore.resetFilters()
    }

    // Service calls
    async getUsers(orgId?: SelectedOrgId): Promise<void> {
        if (!orgId || orgId === "none") {
            return Promise.reject(new Error("Do not call getUsers with 'none' orgId"))
        } else {
            await this.userListService.getUsers(orgId)
        }
    }

    async inviteExternalUser(username: string, applicationTypeIds: number[]): Promise<InviteStatus> {
        return await this.userListService.inviteExternalUser(username, applicationTypeIds)
    }
}
